import Cookies from 'js-cookie';
import { useFlags } from 'launchdarkly-react-client-sdk';

import getEnvironment, { getFeatureFlagOverrides } from './EnvironmentUtil';
import { IFeature } from './interfaces/feature';

export const Features: { [name: string]: IFeature } = {
    /** For feature flags to work in the Auth0 SPA, you must check the Client-Side SDK setting in LD */
    EnableAutomationHubCloudSignup: {
        name: 'enableAutomationHubCloudSignup',
        defaultValue: false,
    },
    EnableNewACText: {
        name: 'enableNewACText',
        defaultValue: false,
    },
    EnableDnBForms: {
        name: 'enableDnBForms',
        defaultValue: false,
    },
    EnableEcommerceIntegration: {
        name: 'enableEcommerceIntegration',
        defaultValue: false,
    },
    EnableEcommercePromotionalDiscounts: {
        name: 'enableEcommercePromotionalDiscounts',
        defaultValue: false,
    },
    /** Enabled Ecommerce - Robot Units support PLT-14703 */
    EnableEcommerceRobotUnits: {
        name: 'enableEcommerceRobotUnits',
        defaultValue: false,
    },
    EnableBillBoard: {
        name: 'enableBillBoard',
        defaultValue: false,
    },
    EnableLocKeysLanguage: {
        name: 'enable-loc-keys-language',
        defaultValue: false,
    },
    EnableEmailVerificationWithCode: {
        name: 'enable-email-verification-with-code',
        defaultValue: false,
    },
    EnableOffersRevamp: {
        name: 'enableOffersRevamp',
        defaultValue: false,
    },
};

const DefaultValuesMap = Object.keys(Features).reduce((accumulator, current) => {
    const value = Features[current];
    accumulator[value.name] = value.defaultValue;
    return accumulator;
}, {} as { [key: string]: boolean });

const env = getEnvironment();
const getFeatureKey = (key: string) => Object.entries(Features).find(([ _, value ]) => value.name === key)?.[0];

export const useFeatureFlagValue = (featureFlagKeys: IFeature[]) => {
    const flags = useFlags();

    const featureFlagOverrides = getFeatureFlagOverrides();
    const featureFlagValues: { [key: string]: boolean } = {};

    featureFlagKeys.forEach((key) => {
        const featureKey = getFeatureKey(key.name) ?? key.name;
        const cookieVal = Features[featureKey].cookieOptIn && Cookies.get(`${key.name}-${env}`);
        if (cookieVal) {
            featureFlagValues[featureKey] = cookieVal.toLowerCase() === 'true';
        } else if (isLocalFeatureFlagsEnabled && localStorage.getItem(key.name) != null) {
            featureFlagValues[featureKey] = localStorage.getItem(key.name)?.toLowerCase() === 'true';
        } else if (!!Object.keys(flags).length && !!flags[key.name]) {
            featureFlagValues[featureKey] = flags[key.name];
        } else {
            featureFlagValues[featureKey] =
            (featureFlagOverrides?.[key.name] != null)
                ? featureFlagOverrides[key.name]
                : DefaultValuesMap[key.name];
        }
    });

    return featureFlagValues;
};

export const isLocalFeatureFlagsEnabled =
    typeof window !== 'undefined' &&
    ((window as any).Cypress || (window as any).env?.FEATURE_FLAGS?.[Features.EnableLocalStorageFeatureFlags.name]?.value);
